import { useCallback, useEffect, useRef, useState } from "react";
import { paymentsApi, purchaseApi } from "@/lib/api/endpoints";
import type { PurchaseAction } from "@/lib/types/config";
import type { PurchaseOrder } from "@/lib/types/api";

type Phase = "start" | "sending" | "waiting" | "paid" | "failed";

const TITLES: Record<PurchaseAction, string> = {
  download: "Download your photo",
  print: "Print your photo",
  share: "Get your link",
};

/**
 * Pay for one action, without leaving the photo.
 *
 * A sheet rather than a screen change: the guest is looking at the thing they
 * want, and taking it away to a separate page to pay loses the reason they were
 * about to buy.
 *
 * The guest types their telebirr number here; telebirr prompts their own
 * handset and they approve with their PIN *there*. Nothing secret is ever typed
 * on the booth. The kiosk just polls until the order settles, then the caller
 * performs the action automatically — no second tap.
 */
export function PaymentSheet({
  slug, action, amountCents, currency, identify, testMode, paymentMode = "web_h5", paymentProvider, fullScreen = false, onPaid, onClose,
}: {
  slug: string;
  action: PurchaseAction;
  amountCents: number;
  currency: string;
  /** Any one of these identifies the photo. The share page has only the token. */
  identify: { generation_id?: number; snapshot_id?: number; public_token?: string };
  testMode: boolean;
  paymentMode?: "web_h5" | "superapp";
  paymentProvider?: string | null;
  /**
   * `false` positions the sheet inside its nearest positioned ancestor, which
   * is what the kiosk's device frame needs. `true` pins it to the viewport, for
   * the share page on a real phone.
   */
  fullScreen?: boolean;
  onPaid: () => void;
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<Phase>("start");
  const [order, setOrder] = useState<PurchaseOrder | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [payerPhone, setPayerPhone] = useState("");

  const settled = useRef(false);

  const amount = `${(amountCents / 100).toFixed(2)} ${currency}`;
  const isArifPay = paymentProvider === "arifpay";
  // Nine digits after an optional leading zero, starting 9 or 7.
  const finish = useCallback(() => {
    if (settled.current) return;
    settled.current = true;
    setPhase("paid");
    // A beat on the confirmation so the guest sees it worked, then act.
    window.setTimeout(onPaid, 900);
  }, [onPaid]);

  async function submit() {
    if (isArifPay && payerPhone.replace(/\D/g, "").length < 9) {
      setError("Enter a valid Telebirr mobile number to receive the payment prompt.");
      return;
    }
    setPhase("sending");
    setError(null);

    try {
      const result = await purchaseApi.create(slug, {
        purchase_action: action,
        ...identify,
        ...(isArifPay ? { payer_phone: payerPhone } : {}),
      });

      if (result.granted || !result.order) return finish();

      setOrder(result.order);
      if (paymentMode === "superapp") {
        if (!result.order.raw_request) throw new Error("Telebirr did not return a payment request");
        if (!window.consumerapp || typeof window.consumerapp.evaluate !== "function") {
          throw new Error("Open this page inside the Telebirr SuperApp");
        }
        window.handleinitDataCallback = () => {
          window.location.reload();
        };
        window.consumerapp.evaluate(JSON.stringify({
          functionName: "js_fun_start_pay",
          params: {
            rawRequest: result.order.raw_request.trim(),
            functionCallBackName: "handleinitDataCallback",
          },
        }));
        setPhase("waiting");
        return;
      }
      if (result.order.checkout_url) {
        window.location.assign(result.order.checkout_url);
        return;
      }
      setPhase("waiting");
    } catch (caught) {
      setPhase("start");
      setError(caught instanceof Error ? caught.message : "Could not send that request");
    }
  }

  // Poll until the provider confirms. The guest's phone never returns here, so
  // polling is the only signal the kiosk gets.
  useEffect(() => {
    if (phase !== "waiting" || !order?.reference) return;

    const id = window.setInterval(() => {
      void paymentsApi.order(order.reference).then((latest) => {
        if (latest.paid) finish();
        else if (latest.status === "expired" || latest.status === "failed") setPhase("failed");
      }).catch(() => undefined);
    }, 1500);

    return () => window.clearInterval(id);
  }, [phase, order?.reference, finish]);

  async function simulate() {
    if (!order?.reference) return;
    try {
      await purchaseApi.simulate(slug, order.reference);
      finish();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not simulate that payment");
    }
  }

  return (
    <div className={`${fullScreen ? "fixed z-50" : "absolute z-30"} inset-0 flex items-end justify-center sm:items-center`}>
      <button
        aria-label="Close"
        onClick={phase === "paid" ? undefined : onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />

      <div className="relative z-10 w-full max-w-sm rounded-t-3xl bg-white p-6 text-black shadow-2xl sm:rounded-3xl">
        <header className="mb-4 flex flex-col gap-1 text-center">
          <span className="text-xs font-semibold uppercase tracking-[0.15em] text-black/50">
            {TITLES[action]}
          </span>
          <span className="text-3xl font-bold tabular-nums">{amount}</span>
        </header>

        {phase === "start" && (
          <>
            <p className="mb-4 text-center text-sm text-black/60">
              {isArifPay
                ? "Enter your Telebirr number. ArifPay will send a payment request to your phone; approve it there."
                : "You will be redirected to the secure Telebirr checkout page to complete payment."}
            </p>

            {isArifPay && (
              <label className="mb-4 block text-left text-xs font-semibold text-black/60">
                Telebirr phone number
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="09XXXXXXXX or +2519XXXXXXXX"
                  value={payerPhone}
                  onChange={(event) => { setPayerPhone(event.target.value); setError(null); }}
                  className="mt-1.5 w-full rounded-xl border border-black/15 bg-white px-4 py-3 text-base font-normal text-black outline-none focus:border-black/50"
                />
              </label>
            )}

            {error && <p className="mb-3 text-center text-sm text-red-600">{error}</p>}

            <button
              onClick={() => void submit()}
              className="mb-2 w-full rounded-xl bg-black py-4 text-base font-bold uppercase tracking-wide text-white disabled:opacity-30"
            >
              {isArifPay ? "Send payment request" : "Continue to Telebirr"}
            </button>
          </>
        )}

        {(phase === "sending" || phase === "waiting") && (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-black/10 border-t-black/70" role="status" aria-label="Loading" />
            <p className="text-sm font-semibold">
              {phase === "sending" ? (isArifPay ? "Sending payment request…" : "Opening Telebirr…") : "Waiting for payment"}
            </p>
            <p className="max-w-[16rem] text-xs leading-relaxed text-black/55">
              {phase === "sending"
                ? (isArifPay ? "Contacting ArifPay to send a USSD prompt to your phone." : "Asking telebirr to prompt your handset.")
                : (isArifPay ? "Approve the USSD payment on your phone. This screen will update when the callback arrives." : "Approve the payment in telebirr. Your photo unlocks the moment it clears.")}
            </p>
            <span className="mt-1 flex items-center gap-1.5 text-xs text-black/40">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-black/40" />
              Waiting for confirmation
            </span>

            {testMode && phase === "waiting" && (
              <button
                onClick={() => void simulate()}
                className="mt-3 rounded-lg border border-dashed border-black/25 px-4 py-2 text-xs font-semibold text-black/60"
              >
                Simulate payment (test mode)
              </button>
            )}
          </div>
        )}

        {phase === "paid" && (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <div className="text-5xl">✅</div>
            <p className="text-base font-bold">Payment confirmed</p>
            <p className="text-xs text-black/55">
              {action === "download" ? "Your download is starting…"
                : action === "print" ? "Sending to the printer…"
                : "Your link is ready…"}
            </p>
          </div>
        )}

        {phase === "failed" && (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <div className="text-5xl">⏳</div>
            <p className="text-sm font-semibold">That payment didn't go through</p>
            <p className="max-w-[16rem] text-xs text-black/55">
              Nothing was charged. Your photo is still here — you can try again.
            </p>
            <button
              onClick={() => { setPhase("start"); setOrder(null); settled.current = false; }}
              className="mt-1 w-full rounded-xl bg-black py-3 text-sm font-bold uppercase text-white"
            >
              Try again
            </button>
          </div>
        )}

        {phase !== "paid" && (
          <button onClick={onClose} className="w-full py-2 text-xs uppercase tracking-wider text-black/40">
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
